import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import {
  LayoutDashboard, ListTodo, ClipboardCheck, Users, Hammer,
  TrendingUp, LogOut, BellDot, Search, Menu, X,
  LayoutGrid, Plus, ShieldCheck, Crown, BarChart3, Activity, Video,
  FileCheck2, Shield, GraduationCap, Lightbulb
} from "lucide-react";
import { cn } from "@/lib/utils";
import AddActionDialog from "@/components/AddActionDialog";
import SuggestionDialog from "@/components/SuggestionDialog";
import OrgSwitchBanner from "@/components/OrgSwitchBanner";
import HubCmdLogo from "@/components/HubCmdLogo";

const NAV = [
  { to: "/app", label: "Command", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/app/actions", label: "Actions", icon: LayoutGrid, testId: "nav-actions" },
  { to: "/app/vv", label: "V&V", icon: FileCheck2, testId: "nav-vv" },
  { to: "/app/checklists", label: "Audits", icon: ClipboardCheck, testId: "nav-checklists" },
  { to: "/app/sops", label: "Doc Library", icon: FileCheck2, testId: "nav-sops" },
  { to: "/app/orgdev", label: "Org-Dev", icon: GraduationCap, testId: "nav-orgdev" },
  { to: "/app/teams", label: "Hubs", icon: Users, testId: "nav-hubs" },
  { to: "/app/meetings", label: "Meetings", icon: Video, testId: "nav-meetings", adminOnly: true },
  { to: "/app/pulse", label: "CMD Health", icon: Activity, testId: "nav-cmd-health", adminOnly: true },
  { to: "/app/admin", label: "Admin", icon: ShieldCheck, testId: "nav-admin", adminOnly: true },
  { to: "/app/controller", label: "Controller", icon: Crown, testId: "nav-controller", controllerOnly: true },
  { to: "/app/master", label: "Master", icon: Crown, testId: "nav-master", masterOnly: true },
];

const MOBILE_NAV = [
  { to: "/app", label: "Home", icon: LayoutDashboard, testId: "mnav-home" },
  { to: "/app/vv", label: "V&V", icon: FileCheck2, testId: "mnav-vv" },
  { to: "/app/checklists", label: "Audits", icon: ClipboardCheck, testId: "mnav-audits" },
  { to: "/app/actions", label: "Actions", icon: LayoutGrid, testId: "mnav-actions" },
  { to: "/app/teams", label: "Hubs", icon: Users, testId: "mnav-hubs" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const loc = useLocation();
  const nav = useNavigate();
  const [drawer, setDrawer] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [isMaster, setIsMaster] = useState(false);

  useEffect(() => {
    api.get("/master/whoami").then((r) => setIsMaster(!!r.data?.is_master)).catch(() => {});
  }, []);

  const initials = (user?.name || "")
    .split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const SidebarInner = () => (
    <>
      <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-[#ff4500]/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-10 w-64 h-64 rounded-full bg-[#2a2a2a]/40 blur-3xl pointer-events-none" />
      <div className="relative px-5 py-5 flex items-center gap-3">
        <HubCmdLogo size={42} variant="shield" inverted />
        <div>
          <div className="font-display font-black text-lg tracking-tight leading-none">
            <span>hub</span><span className="text-[#ff5f15]">CMD</span>
          </div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 mt-1">Operations · Compliance</div>
        </div>
        <button onClick={() => setDrawer(false)} className="ml-auto lg:hidden w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center">
          <X className="w-4 h-4" />
        </button>
      </div>
      <nav className="relative flex-1 px-3 py-2 space-y-1 overflow-auto scrollbar-thin">
        {NAV.filter((n) => {
          if (n.adminOnly && !["admin", "controller"].includes(user?.org_role) && !isMaster) return false;
          if (n.controllerOnly && !(user?.org_role === "controller") && !isMaster) return false;
          if (n.masterOnly && !isMaster) return false;
          return true;
        }).map((n) => {
          const Icon = n.icon;
          const active = loc.pathname === n.to || (n.to !== "/app" && loc.pathname.startsWith(n.to));
          return (
            <Link key={n.to} to={n.to} onClick={() => setDrawer(false)} data-testid={n.testId}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 group relative",
                active ? "bg-white/15 text-white shadow-innernavy" : "text-white/70 hover:bg-white/10 hover:text-white"
              )}>
              <span className={cn(
                "w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-150",
                active ? "bg-[#ff4500] text-white" : "bg-white/10 text-white/80 group-hover:bg-white/15"
              )}>
                <Icon className="w-4 h-4" />
              </span>
              <span className="uppercase tracking-wider text-[11px] font-bold flex-1">{n.label}</span>
              {n.desktopOnly && <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-white/10 text-white/60">DESK</span>}
            </Link>
          );
        })}
      </nav>
      <div className="relative px-3 pb-4">
        <div className="bg-white/10 rounded-2xl p-3 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#ff4500] to-[#ff5f15] flex items-center justify-center text-white font-display font-black text-sm">{initials || "•"}</div>
            <div className="min-w-0 flex-1">
              <div className="font-display font-bold text-sm truncate" data-testid="current-user-name">{user?.name}</div>
              {user?.title ? (
                <div className="text-[10px] text-white/60 truncate font-mono">{user.title}</div>
              ) : (
                <div className="text-[10px] text-white/60 truncate font-mono">{user?.email}</div>
              )}
              {user?.org && (
                <div className="text-[10px] text-[#ff5f15] truncate font-mono uppercase tracking-wider mt-0.5" data-testid="current-org">
                  {user.org.name} · {user.org_role}
                </div>
              )}
            </div>
          </div>
          <button onClick={() => { logout(); nav("/login"); }} data-testid="logout-button"
            className="mt-3 w-full flex items-center justify-center gap-2 bg-white/0 border border-white/25 text-white text-[10px] uppercase tracking-[0.18em] font-bold py-2 rounded-xl hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors duration-150">
            <LogOut className="w-3 h-3" /> Sign out
          </button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex text-[#1a1a1a] app-shell-bg">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-[#1a1a1a] text-white flex-col relative overflow-hidden" data-testid="app-sidebar">
        <SidebarInner />
      </aside>

      {/* Mobile drawer */}
      {drawer && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setDrawer(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-72 bg-[#1a1a1a] text-white flex flex-col overflow-hidden">
            <SidebarInner />
          </aside>
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white border-b border-[#1a1a1a]/10 flex items-center px-4 lg:px-6 justify-between shadow-soft">
          <div className="flex items-center gap-3">
            <button data-testid="mobile-drawer-toggle" onClick={() => setDrawer(true)} className="lg:hidden w-10 h-10 rounded-xl bg-[#f7f5f2] border border-[#1a1a1a]/10 flex items-center justify-center">
              <Menu className="w-4 h-4 text-[#1a1a1a]" />
            </button>
            <div className="w-2 h-8 rounded-full bg-[#ff4500] hidden lg:block" />
            <div>
              <div className="font-display font-black text-base tracking-tight">{currentTitle(loc.pathname)}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#1a1a1a]/55 hidden sm:block">{user?.company} · {user?.role}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden xl:flex items-center gap-2 bg-[#f7f5f2] border border-[#1a1a1a]/10 rounded-full px-3 py-1.5">
              <Search className="w-3.5 h-3.5 text-[#1a1a1a]/50" />
              <input data-testid="header-search-input" placeholder="Search…" className="bg-transparent outline-none text-sm w-48 placeholder:text-[#1a1a1a]/40" />
            </div>
            <button data-testid="header-add-action" onClick={() => setAddOpen(true)} className="hidden md:inline-flex btn-pill btn-pill-primary">
              <Plus className="w-3.5 h-3.5" /> Add action
            </button>
            <button data-testid="header-suggest" onClick={() => setSuggestOpen(true)}
              className="hidden md:inline-flex btn-pill btn-pill-secondary" title="Suggest an improvement or report an issue">
              <Lightbulb className="w-3.5 h-3.5" /> Suggest / Report
            </button>
            <button data-testid="header-notifications" className="relative w-10 h-10 rounded-full bg-[#f7f5f2] border border-[#1a1a1a]/10 flex items-center justify-center hover:bg-[#fff1ea]">
              <BellDot className="w-4 h-4 text-[#1a1a1a]" />
              <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-[#ff4500]" />
            </button>
          </div>
        </header>
        <div className="flex-1 overflow-auto pb-24 lg:pb-0"><OrgSwitchBanner />{children}</div>
      </main>

      {/* Mobile bottom nav + FAB */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-[#1a1a1a]/10 shadow-lift">
        <div className="flex items-center justify-around relative">
          {MOBILE_NAV.slice(0, 2).map((n) => <MobileNavItem key={n.to} n={n} active={loc.pathname === n.to} />)}
          <button data-testid="mobile-fab-add" onClick={() => setAddOpen(true)} className="-mt-7 w-14 h-14 rounded-full bg-[#ff4500] text-white shadow-glow flex items-center justify-center hover:bg-[#cc3500]">
            <Plus className="w-6 h-6" />
          </button>
          {MOBILE_NAV.slice(2, 5).map((n) => <MobileNavItem key={n.to} n={n} active={loc.pathname.startsWith(n.to) && (n.to !== "/app" || loc.pathname === "/app")} />)}
        </div>
      </nav>

      <AddActionDialog open={addOpen} onClose={() => setAddOpen(false)} onCreated={() => {}} />
      <SuggestionDialog open={suggestOpen} onClose={() => setSuggestOpen(false)} />
    </div>
  );
}

function MobileNavItem({ n, active }) {
  const Icon = n.icon;
  return (
    <Link to={n.to} data-testid={n.testId} className={`flex-1 py-2.5 flex flex-col items-center gap-0.5 ${active ? "text-[#ff4500]" : "text-[#1a1a1a]/70"}`}>
      <Icon className="w-4 h-4" />
      <span className="text-[10px] uppercase tracking-wider font-bold">{n.label}</span>
    </Link>
  );
}

function currentTitle(p) {
  if (p === "/app") return "Command Center";
  if (p.startsWith("/app/actions")) return "Actions";
  if (p.startsWith("/app/vv")) return "Verification & Validation";
  if (p.startsWith("/app/sops")) return "Doc Library";
  if (p.startsWith("/app/orgdev")) return "Org-Dev";
  if (p.startsWith("/app/tasks")) return "Task Ledger";
  if (p.startsWith("/app/checklists")) return "Checklists & Audits";
  if (p.startsWith("/app/reports")) return "Trends & Reports";
  if (p.startsWith("/app/meetings")) return "Meetings";
  if (p.startsWith("/app/teams")) return "Hubs & Chat";
  if (p.startsWith("/app/work-orders")) return "Work Orders";
  if (p.startsWith("/app/admin")) return "Organization Admin";
  if (p.startsWith("/app/me/analytics")) return "My Analytics";
  if (p.startsWith("/app/people/") && p.endsWith("/analytics")) return "Member Analytics";
  if (p.startsWith("/app/pulse")) return "CMD Health";
  return "Operations";
}
